import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Rocket,
  Loader2,
  ExternalLink,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { useDeployments } from "../../hooks/useDeployments";
import { useWorkspaceCommits } from "../../hooks/useWorkspaceCommits";
import { useSettingsModal } from "../../contexts/SettingsModalContext";
import { useReadOnlyMode } from "../../hooks/useReadOnlyMode";

interface PublishMenuProps {
  workspaceId: string;
  deployedUrl?: string;
  templateSlug?: string;
}

const PublishMenu: React.FC<PublishMenuProps> = ({
  workspaceId,
  deployedUrl,
  templateSlug,
}) => {
  const { openProjectSettings } = useSettingsModal();
  const isExpoTemplate = templateSlug === "expo-template";

  const [publishMenuOpen, setPublishMenuOpen] = useState(false);
  const publishMenuRef = useRef<HTMLDivElement>(null);

  const {
    deployments,
    loading: deploymentsLoading,
    creatingDeployment,
    fetchDeployments,
    createDeployment,
  } = useDeployments(workspaceId);

  const {
    commits,
    loading: commitsLoading,
    refetch: refetchCommits,
  } = useWorkspaceCommits(workspaceId, 1);

  const sortedDeployments = useMemo(() => {
    return [...deployments].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [deployments]);

  const isDeploymentRunning = useMemo(
    () =>
      deployments.some(
        (d) => d.status === "RUNNING" || d.status === "NOT_STARTED",
      ) || creatingDeployment,
    [deployments, creatingDeployment],
  );

  useEffect(() => {
    if (!workspaceId) return;
    if (isDeploymentRunning) {
      const intervalId = setInterval(() => {
        fetchDeployments();
      }, 3000);
      return () => clearInterval(intervalId);
    }
  }, [isDeploymentRunning, fetchDeployments, workspaceId]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (
        publishMenuRef.current &&
        !publishMenuRef.current.contains(e.target as Node)
      ) {
        setPublishMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Read-only mode (must be called unconditionally at top-level of component)
  const { isReadOnly } = useReadOnlyMode();

  const handlePublishLatest = useCallback(async () => {
    if (!workspaceId || !commits || commits.length === 0 || creatingDeployment)
      return;
    try {
      await createDeployment(commits[0].id);
      fetchDeployments();
      refetchCommits();
    } catch (err) {
      console.error("Failed to create deployment:", err);
    }
  }, [
    workspaceId,
    commits,
    creatingDeployment,
    createDeployment,
    fetchDeployments,
    refetchCommits,
  ]);

  if (!workspaceId) return null;

  return (
    <div className="relative" ref={publishMenuRef}>
      <button
        className={`inline-flex items-center px-2 py-1.5 text-sm font-medium rounded transition-all ${
          isReadOnly
            ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
            : `bg-brand-action hover:bg-brand-action-hover text-brand-action-text ${publishMenuOpen ? "ring-2 ring-brand-action/40 shadow-md" : ""}`
        }`}
        onClick={() => { if (!isReadOnly) setPublishMenuOpen((prev) => !prev); }}
        aria-haspopup="true"
        aria-expanded={publishMenuOpen}
        data-tour="publish-menu"
        data-publish-trigger="true"
        title={
          isReadOnly ? 'View-only mode: upgrade to publish' : (isDeploymentRunning ? "Publish in progress..." : "Publish options")
        }
      >
        {isDeploymentRunning ? (
          <Loader2 size={16} className="mr-1" />
        ) : (
          <Rocket size={16} className="mr-1" />
        )}
        <span className="hidden sm:inline">Publish</span>
        <ChevronDown
          size={12}
          className={`ml-1 transition-transform ${publishMenuOpen ? "rotate-180" : ""}`}
        />
      </button>

      {publishMenuOpen && (
        <div className="absolute right-0 mt-2 w-64 z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/30">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
                  Publish
                </h3>
                {isDeploymentRunning && (
                  <div className="flex items-center text-xs text-neutral-500 dark:text-neutral-400">
                    <Loader2 size={12} className="mr-1 animate-spin" />
                    Publish in progress...
                  </div>
                )}
              </div>
            </div>

            <div className="py-1">
              {isDeploymentRunning ? (
                <button
                  onClick={() => {
                    setPublishMenuOpen(false);
                    openProjectSettings(workspaceId, "publish");
                  }}
                  className={`flex items-center gap-2 w-full text-left px-4 py-2 text-sm transition-colors text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60`}
                >
                  <RefreshCw size={14} className="text-neutral-500" />
                  Show deployment
                </button>
              ) : (
                <button
                  onClick={async () => {
                    setPublishMenuOpen(false);
                    await handlePublishLatest();
                  }}
                  disabled={
                    commitsLoading || deploymentsLoading || !commits?.length
                  }
                  className={`flex items-center gap-2 w-full text-left px-4 py-2 text-sm transition-colors ${
                    commitsLoading || deploymentsLoading || !commits?.length
                      ? "text-neutral-400 dark:text-neutral-500 cursor-not-allowed"
                      : "text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
                  }`}
                  data-tour="publish-latest"
                >
                  <Rocket size={14} className="text-neutral-500" />
                  Publish latest version
                </button>
              )}

              {deployedUrl && !isExpoTemplate && (
                <a
                  href={deployedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors"
                  data-tour="open-deployed-app"
                >
                  <ExternalLink size={14} className="text-neutral-500" />
                  Open app
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublishMenu;
